import { FC, useEffect, useState, MouseEvent } from "react";
import { styled } from "@mui/material/styles";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { formatDateTime } from "../../../../util/common-functions";
import { AddStationDrawer } from "../../../../components/admin/add-new-station-drawer/add-new-station-drawer";
import { 
  getAllStationsWithStatusAction,
  deactivateStationAction,
  activateStationAction 
} from "../../../../redux/actions/cashierStationActions";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export const StationManagement: FC = () => {
  const dispatch = useAppDispatch();
  const { 
    allStationsWithStatus, 
    getAllStationsWithStatusLoading 
  } = useAppSelector((state) => state.cashierStation);
  const { addStationData } = useAppSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPoint, setSelectedPoint] = useState<any | null>(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    action: () => {},
  });

  const snackBar = useSnackbarContext();

  // Filter options
  const statusFilterOptions = [
    { value: "all", label: "All Statuses" },
    { value: "Available", label: "Available" },
    { value: "Occupied (Assigned)", label: "Occupied (Assigned)" },
    { value: "Disabled", label: "Disabled" },
  ];

  // Filter stations based on selected status and search term
  const filteredStations = allStationsWithStatus?.filter((station) => {
    // Filter by status
    const statusMatch = statusFilter === "all" || station.occupationStatus === statusFilter;
    
    // Filter by search term (case-insensitive search in station name)
    const searchMatch = searchTerm === "" || 
      station.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return statusMatch && searchMatch;
  }) || [];

  useEffect(() => {
    dispatch(getAllStationsWithStatusAction(1));
  }, []);

  useEffect(() => {
    dispatch(getAllStationsWithStatusAction(1));
  }, [addStationData]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>, point: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedPoint(point);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOnDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedPoint(null);
  };

  const handleDisableStation = (station: any) => {
    setConfirmDialog({
      open: true,
      title: "Disable Station",
      message: `Are you sure you want to disable station "${station.name}"? This will make it unavailable for device onboarding.`,
      action: () => {
        dispatch(deactivateStationAction(station.id))
          .unwrap()
          .then(() => {
            snackBar.showSnackbar(`Station "${station.name}" has been disabled successfully`, "success");
            setConfirmDialog({ ...confirmDialog, open: false });
          })
          .catch((error) => {
            snackBar.showSnackbar(`Failed to disable station: ${error}`, "error");
            setConfirmDialog({ ...confirmDialog, open: false });
          });
      },
    });
    setAnchorEl(null);
  };

  const handleEnableStation = (station: any) => {
    setConfirmDialog({
      open: true,
      title: "Enable Station",
      message: `Are you sure you want to enable station "${station.name}"? This will make it available for device onboarding.`,
      action: () => {
        dispatch(activateStationAction(station.id))
          .unwrap()
          .then(() => {
            snackBar.showSnackbar(`Station "${station.name}" has been enabled successfully`, "success");
            setConfirmDialog({ ...confirmDialog, open: false });
          })
          .catch((error) => {
            snackBar.showSnackbar(`Failed to enable station: ${error}`, "error");
            setConfirmDialog({ ...confirmDialog, open: false });
          });
      },
    });
    setAnchorEl(null);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "3rem",
          height: "57.5rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
          maxHeight: "57.5rem",
        }}
      >
        <AddStationDrawer
          open={openDrawer}
          toggleDrawer={handleOnDrawerClose}
          populateFor={
            selectedPoint
              ? {
                  id: selectedPoint.id,
                  deviceName: selectedPoint.name,
                }
              : undefined
          }
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
            Station Management
          </Typography>
          <CustomButton
            sx={{ width: "11rem", height: "3rem" }}
            onClick={() => {
              setOpenDrawer(true);
              setSelectedPoint(null);
            }}
          >
            Add New
          </CustomButton>
        </Box>

        {/* Filter Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "2rem",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            {/* Search Field */}
            <TextField
              placeholder="Search stations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                minWidth: "200px",
                "& .MuiOutlinedInput-root": {
                  height: "3rem",
                },
              }}
            />
            
            {/* Status Filter */}
            <Typography variant="body1" sx={{ color: Colors.TEXT_GRAY_DARK, fontWeight: 500 }}>
              Status:
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{
                  height: "3rem",
                  "& .MuiOutlinedInput-root": {
                    height: "3rem",
                  },
                }}
              >
                {statusFilterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ color: Colors.TEXT_GRAY_DARK }}>
              Showing {filteredStations.length} of {allStationsWithStatus?.length || 0} stations
            </Typography>
            
            {/* Active Filter Indicators */}
            {(statusFilter !== "all" || searchTerm !== "") && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                {searchTerm && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      padding: "0.25rem 0.5rem",
                      backgroundColor: "#e3f2fd",
                      borderRadius: "4px",
                      fontSize: "0.75rem",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#1976d2" }}>
                      Search: "{searchTerm}"
                    </Typography>
                  </Box>
                )}
                
                {statusFilter !== "all" && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      padding: "0.25rem 0.5rem",
                      backgroundColor: "#f3e5f5",
                      borderRadius: "4px",
                      fontSize: "0.75rem",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#7b1fa2" }}>
                      Status: {statusFilterOptions.find(opt => opt.value === statusFilter)?.label}
                    </Typography>
                  </Box>
                )}
                
                <CustomButton
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setStatusFilter("all");
                    setSearchTerm("");
                  }}
                  sx={{ 
                    height: "2rem", 
                    fontSize: "0.875rem",
                    minWidth: "auto",
                    padding: "0 1rem"
                  }}
                >
                  Clear All
                </CustomButton>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            maxHeight: "51rem",
            width: "100%",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "0.5rem" },
            "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
            "&::-webkit-scrollbar-thumb": { background: "#888" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
          }}
        >
          <Table sx={{ width: "100%" }} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Created Time</StyledTableCell>
                <StyledTableCell>Updated Time</StyledTableCell>
                <StyledTableCell>Active Status</StyledTableCell>
                <StyledTableCell>Occupation Status</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getAllStationsWithStatusLoading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={6} sx={{ textAlign: "center" }}>
                    <CircularProgress size={24} color="inherit" />
                  </StyledTableCell>
                </StyledTableRow>
              )}

              {!getAllStationsWithStatusLoading &&
                filteredStations.map((station) => (
                  <StyledTableRow key={station.id}>
                    <StyledTableCell>{station.name}</StyledTableCell>
                    <StyledTableCell>
                      {formatDateTime(station.createdTime)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {formatDateTime(station.updatedTime)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          backgroundColor: station.isActive ? "#e8f5e8" : "#f8d7da",
                          color: station.isActive ? "#155724" : "#721c24",
                        }}
                      >
                        {station.isActive ? "Active" : "Inactive"}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          backgroundColor: 
                            station.occupationStatus === "Available" 
                              ? "#e8f5e8" 
                              : station.occupationStatus === "Occupied (Assigned)"
                              ? "#fff3cd"
                              : "#f8d7da",
                          color: 
                            station.occupationStatus === "Available"
                              ? "#155724"
                              : station.occupationStatus === "Occupied (Assigned)"
                              ? "#856404"
                              : "#721c24",
                        }}
                      >
                        {station.occupationStatus}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: station.isActive ? "#4caf50" : "#f44336",
                          }}
                        />
                        <IconButton onClick={(e) => handleClick(e, station)}>
                          <MoreHorizIcon
                            sx={{ fontSize: "2rem" }}
                            color="primary"
                          />
                        </IconButton>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}

              {!getAllStationsWithStatusLoading && filteredStations.length === 0 && (
                <StyledTableRow>
                  <StyledTableCell colSpan={6} sx={{ textAlign: "center" }}>
                    {allStationsWithStatus?.length === 0 
                      ? "No stations found." 
                      : statusFilter === "all" && searchTerm === ""
                      ? "No stations found."
                      : `No stations found matching your filters. ${
                          searchTerm ? `Search: "${searchTerm}"` : ""
                        } ${
                          statusFilter !== "all" 
                            ? `Status: "${statusFilterOptions.find(opt => opt.value === statusFilter)?.label}"` 
                            : ""
                        }`
                    }
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          MenuListProps={{ "aria-labelledby": "basic-button" }}
        >
          <MenuItem
            onClick={() => snackBar.showSnackbar("Edit coming soon!", "info")}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Station</ListItemText>
          </MenuItem>
          
          {selectedPoint?.isActive ? (
            <MenuItem
              onClick={() => handleDisableStation(selectedPoint)}
              sx={{ color: Colors.ERROR_RED }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: Colors.ERROR_RED }} />
              </ListItemIcon>
              <ListItemText>Disable Station</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => handleEnableStation(selectedPoint)}
              sx={{ color: Colors.SUCCESS_GREEN }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" sx={{ color: Colors.SUCCESS_GREEN }} />
              </ListItemIcon>
              <ListItemText>Enable Station</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
        >
          <DialogTitle id="confirm-dialog-title">
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-dialog-description">
              {confirmDialog.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <CustomButton onClick={handleCloseConfirmDialog} variant="outlined">
              Cancel
            </CustomButton>
            <CustomButton 
              onClick={confirmDialog.action}
              sx={{ 
                backgroundColor: confirmDialog.title.includes("Disable") 
                  ? Colors.ERROR_RED 
                  : Colors.SUCCESS_GREEN,
                "&:hover": {
                  backgroundColor: confirmDialog.title.includes("Disable") 
                    ? "#b71c1c" 
                    : "#1b5e20"
                }
              }}
            >
              {confirmDialog.title.includes("Disable") ? "Disable" : "Enable"}
            </CustomButton>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
